/**
 * Setting Renderer Registry
 *
 * Registry pattern for mapping setting types to their renderer components.
 * This allows dynamic rendering of settings based on their type.
 */

import type { ComponentType } from 'react';
import type { SettingData } from '@/types/nodeSettings';

/**
 * Common props passed to all setting renderers
 */
export interface SettingRendererProps<T = unknown> {
  /** The setting data with isSupported flag and value */
  setting: SettingData<T>;
  /** Callback when setting value changes */
  onChange: (value: T | undefined) => void;
  /** Whether the renderer is in read-only mode */
  readOnly?: boolean;
  /** Label to display */
  label?: string;
  /** Description/help text */
  description?: string;
}

/**
 * Setting type identifiers for registry lookup
 */
export type SettingType =
  | 'boolean'
  | 'timeout'
  | 'retryPolicy'
  | 'concurrency'
  | 'runAfter'
  | 'trackedProperties'
  | 'splitOn'
  | 'correlation'
  | 'requestOptions'
  | 'paging'
  | 'chunking'
  | 'count'
  | 'conditions';

/**
 * Registry mapping setting types to their renderer components
 */
type RendererRegistry = {
  [K in SettingType]?: ComponentType<SettingRendererProps<unknown>>;
};

// Initialize with empty registry - renderers register themselves on import
const registry: RendererRegistry = {};

/**
 * Register a setting renderer
 */
export function registerSettingRenderer<T>(
  type: SettingType,
  component: ComponentType<SettingRendererProps<T>>
) {
  registry[type] = component as ComponentType<SettingRendererProps<unknown>>;
}

/**
 * Get a setting renderer by type
 */
export function getSettingRenderer(
  type: SettingType
): ComponentType<SettingRendererProps<unknown>> | undefined {
  return registry[type];
}

/**
 * Get all registered renderer types
 */
export function getRegisteredTypes(): SettingType[] {
  return Object.keys(registry) as SettingType[];
}

/**
 * Export the registry for direct access
 */
export const settingRendererRegistry = {
  register: registerSettingRenderer,
  get: getSettingRenderer,
  getTypes: getRegisteredTypes,
};
